import { useCallback, useMemo } from 'react';

import { useChartTheme } from '../../utilities';

export const ICICLE_ANIMATION_DURATION_MS = 480;
export const ICICLE_LABEL_FONT_SIZE = 12;
export const ICICLE_LABEL_FONT_WEIGHT = 401;
export const ICICLE_LABEL_PADDING_X = 14;
export const ICICLE_MIN_LABEL_HEIGHT = 16;

const PALETTE_FALLBACK = '#63a2d9';
const FONT_FAMILY_FALLBACK = "'Source Sans Pro',sans-serif";

type ColorPlanNode = {
  id: string;
  parentId: string | null;
  depth: number;
  isOther?: boolean;
};

type ColorPlan = {
  branchDepth: number;
  slots: Map<string, number>;
};

function resolveToken(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.body).getPropertyValue(name).trim();
  return value || fallback;
}

export function ancestorAtDepth<T extends ColorPlanNode>(
  node: T,
  byId: Map<string, T>,
  depth: number
): T {
  const seen = new Set<string>();
  let current = node;
  while (current.depth > depth && !seen.has(current.id)) {
    seen.add(current.id);
    const parent =
      current.parentId !== null ? byId.get(current.parentId) : undefined;
    if (!parent) break;
    current = parent;
  }
  return current;
}

export function buildColorPlan(nodes: readonly ColorPlanNode[]): ColorPlan {
  const countByDepth = new Map<number, number>();
  let maxDepth = 0;
  for (const node of nodes) {
    if (node.depth === 0) continue;
    countByDepth.set(node.depth, (countByDepth.get(node.depth) ?? 0) + 1);
    maxDepth = Math.max(maxDepth, node.depth);
  }
  let branchDepth = maxDepth;
  for (let depth = 1; depth <= maxDepth; depth++) {
    if ((countByDepth.get(depth) ?? 0) > 1) {
      branchDepth = depth;
      break;
    }
  }
  const slots = new Map<string, number>();
  for (const node of nodes) {
    if (node.depth === branchDepth && node.isOther !== true) {
      slots.set(node.id, slots.size);
    }
  }
  return { branchDepth, slots };
}

export function useIcicleTheme() {
  const { theme, breakdownPalette } = useChartTheme();
  const fontFamily = useMemo(
    () => resolveToken('--ui-font-family-sans', FONT_FAMILY_FALLBACK),
    []
  );
  const colorForSlot = useCallback(
    (slot: number): string =>
      breakdownPalette[slot % breakdownPalette.length] ??
      breakdownPalette[0] ??
      PALETTE_FALLBACK,
    [breakdownPalette]
  );
  return { theme, fontFamily, colorForSlot };
}
